/**
 * Pure logic for the quotation editor's BATCH furniture edit panel (REQ-403).
 *
 * The panel edits several furniture objects at once, so every field has to answer two questions
 * before it can render: does the whole selection agree on a value (→ show it and behave like the
 * single-object sidebar), or do the values differ (→ show the literal `Mixed`)? And which sections
 * exist at all for this selection — the intersection of its property sets.
 *
 * Everything here is data-in / data-out so it can be unit-tested without the viewer.
 */

import {
	isCountertop,
	isCustomFurniture,
	isOtherFurniture,
} from "components/ui/customWebviewer/FurnitureModelLabel/furnitureModel.helpers"
import type {
	TDimensionGroup,
	TEref,
	TFurniture,
	TFurnitureCustomization,
	TFurnitureDeliveryDate,
	TSelectedModelNumber,
} from "types/elevationReferences.type"
import type { TCustomModelNumberSummary } from "types/customModelNumbers.type"
import type { ICategoriesData } from "types/configuration.type"

/**
 * Marker for "the selected objects hold differing values for this field".
 *
 * A unique symbol rather than the string `"Mixed"` on purpose: Notes and Name are free text, so a
 * user can legitimately type the word "Mixed" and a string sentinel would then be indistinguishable
 * from a real shared value.
 */
export const MIXED: unique symbol = Symbol("batch-mixed")
export type TMixed = typeof MIXED

/** Either the one value the whole selection shares, or {@link MIXED}. */
export type TBatchValue<T> = T | TMixed

export const isMixed = <T>(value: TBatchValue<T>): value is TMixed => value === MIXED

/** The text every `Mixed` field renders — as a value, a placeholder or a chip. */
export const MIXED_LABEL = "Mixed"

/**
 * Whether a blurred field is worth committing.
 *
 * A `Mixed` field starts EMPTY, so "untouched" and "deliberately cleared" look identical — and for
 * notes a clear means the DELETE variant, which would wipe every selected object's comment on a
 * blur the user never meant as an edit. So a Mixed field commits only once it holds something, and a
 * shared field only once it differs from what the objects already hold.
 */
export const isFieldDirty = <T>(shared: TBatchValue<T>, current: T, empty: T): boolean =>
	isMixed(shared) ? current !== empty : current !== shared

/**
 * The single value `pick` returns for every item, or {@link MIXED} when they differ.
 *
 * An EMPTY list reports MIXED: it has no shared value, and reporting one would let a panel display
 * a value nothing in the selection actually holds. (The panel never renders for an empty
 * selection — it needs two or more objects — so this is a safety net, not a real state.)
 */
export const sharedValue = <TItem, TValue>(
	items: readonly TItem[],
	pick: (item: TItem) => TValue,
	isEqual: (a: TValue, b: TValue) => boolean = Object.is,
): TBatchValue<TValue> => {
	if (items.length === 0) return MIXED
	const first = pick(items[0])
	for (let i = 1; i < items.length; i += 1) {
		if (!isEqual(first, pick(items[i]))) return MIXED
	}
	return first
}

/**
 * What KIND of objects the selection holds, which decides the sections the panel renders.
 * `"mixed"` means the selection spans more than one kind — the case the requirement calls out as
 * needing an adapting UI. An empty selection reports `"mixed"` (nothing is common to nothing).
 *
 * The per-item classification reuses the viewer's existing rules verbatim: a countertop is one
 * whose top-level category is Countertops, and an "Other" object is one the backend marked by
 * clearing all three category levels.
 */
export type TBatchSelectionKind = "cabinet" | "countertop" | "custom" | "other" | "mixed"

export const classifyFurniture = (item: TFurniture): Exclude<TBatchSelectionKind, "mixed"> => {
	// Custom is tested first: a custom object has no category either, so `isOtherFurniture` would
	// claim it if that check ran first — which is exactly the confusion the helper's own exclusion
	// exists to prevent.
	if (isCustomFurniture(item)) return "custom"
	if (isOtherFurniture(item)) return "other"
	if (isCountertop(item)) return "countertop"
	return "cabinet"
}

export const resolveBatchSelectionKind = (furniture: readonly TFurniture[]): TBatchSelectionKind => {
	if (furniture.length === 0) return "mixed"
	const first = classifyFurniture(furniture[0])
	for (let i = 1; i < furniture.length; i += 1) {
		if (classifyFurniture(furniture[i]) !== first) return "mixed"
	}
	return first
}

/** Which sections the panel renders — the intersection of the selection's property sets. */
export type TBatchSectionVisibility = {
	/** Model number: only when EVERY selected object is a cabinet. */
	modelNumber: boolean
	/** Attachments (finished ends, fillers, locks, adjustable shelves): cabinets only. */
	attachments: boolean
	/** REQ-449 — Grommets / Wall Cleats counts: countertops only. */
	countertopAttachments: boolean
	/** Name + Price: only when EVERY selected object is Other / fixed-price. */
	nameAndPrice: boolean
	/** Super + Object category selects: not for a mixed-type selection and not for Other. */
	lowerCategories: boolean
}

export const resolveBatchSections = (kind: TBatchSelectionKind): TBatchSectionVisibility => ({
	// A custom selection keeps the model number section — its definition IS its model number — but
	// has no attachments (no estimation config), no name or price of its own (both come from the
	// definition) and no lower categories (it has no category at all).
	modelNumber: kind === "cabinet" || kind === "custom",
	attachments: kind === "cabinet",
	countertopAttachments: kind === "countertop",
	nameAndPrice: kind === "other",
	lowerCategories: kind === "cabinet" || kind === "countertop",
})

/** Every field the batch panel renders, already resolved to a shared value or {@link MIXED}. */
export type TBatchFurnitureFields = {
	erefId: TBatchValue<number | null>
	deliveryDate: TBatchValue<TFurnitureDeliveryDate | null>
	modelNumber: TBatchValue<TSelectedModelNumber | null>
	/** #1346 — the linked Custom Model Number, for an all-custom selection. */
	customModelNumber: TBatchValue<TCustomModelNumberSummary | null>
	width: TBatchValue<number | null>
	height: TBatchValue<number | null>
	depth: TBatchValue<number | null>
	adjustedShelves: TBatchValue<number | null>
	hasLocks: TBatchValue<boolean>
	fillings: TBatchValue<number>
	finishingEnds: TBatchValue<number>
	/**
	 * REQ-449. The backend value itself is non-nullable (defaults to 0), but typed
	 * `number | null` here like `adjustedShelves` — not `number` like `fillings`/`finishingEnds`
	 * — because the batch panel commits it on BLUR through a free-entry `NumberInputRow`
	 * (`isFieldDirty`'s dirty-check needs a matching `null` "nothing typed yet" sentinel), not on
	 * every click through a `SegmentedCountRow` the way fillings/finishingEnds do.
	 */
	countertopGrommets: TBatchValue<number | null>
	countertopWallCleats: TBatchValue<number | null>
	notes: TBatchValue<string>
	name: TBatchValue<string>
	price: TBatchValue<number | null>
	topLevelCategory: TBatchValue<string | null>
	superCategory: TBatchValue<string | null>
	objectCategoryId: TBatchValue<number | null>
	objectCategoryName: TBatchValue<string | null>
	status: TBatchValue<TFurniture["status"]>
	/**
	 * REQ-431, gated PER FIELD like the category levels: `hasCustomization` says whether the
	 * selection agrees on HAVING one at all (a selection split between "has one" and "has none" is
	 * MIXED here even if, by coincidence, the note/price below happen to line up), while the note and
	 * price are compared independently — a selection can share one and disagree on the other, and
	 * only the disagreeing one should read as `Mixed`.
	 */
	hasCustomization: TBatchValue<boolean>
	customizationNote: TBatchValue<string>
	customizationPriceAdjustment: TBatchValue<number | null>
}

/**
 * Resolves every field at once. Model numbers are compared by ID (two cache entries for the same
 * model number are different objects); everything else compares by value.
 *
 * The attachment fields fall back to the same defaults the single-object form uses for a furniture
 * with no `estimation_config` yet, so "no config" and "a config holding the defaults" count as the
 * same value rather than reading as Mixed.
 */
export const deriveBatchFields = (furniture: readonly TFurniture[]): TBatchFurnitureFields => ({
	erefId: sharedValue(furniture, (f) => f.eref_id),
	// Compared by id AND is_inherited, not just id: an explicit override that happens to match the
	// object's own elevation's date is still a DIFFERENT setup from one that is genuinely inherited,
	// even though the two would otherwise look identical (PR #1394 review) — the data sources
	// differ, and only one of the two carries something "Inherit from elevation" can clear. Treating
	// them as equal would show one date with an "Inherited" tag whose truth depended on which
	// furniture happened to sort first, instead of MIXED.
	deliveryDate: sharedValue(
		furniture,
		(f) => f.delivery_date ?? null,
		(a, b) => (a?.id ?? null) === (b?.id ?? null) && (a?.is_inherited ?? null) === (b?.is_inherited ?? null),
	),
	modelNumber: sharedValue(
		furniture,
		(f) => f.selected_model_number,
		(a, b) => (a?.id ?? null) === (b?.id ?? null),
	),
	// Compared by id for the same reason as the Stevens one: two cache entries for one definition
	// are different objects.
	customModelNumber: sharedValue(
		furniture,
		(f) => f.custom_object_config?.custom_model_number ?? null,
		(a, b) => (a?.id ?? null) === (b?.id ?? null),
	),
	width: sharedValue(furniture, (f) => f.width),
	height: sharedValue(furniture, (f) => f.height),
	depth: sharedValue(furniture, (f) => f.depth),
	adjustedShelves: sharedValue(furniture, (f) => f.estimation_config?.adjusted_shelves ?? null),
	hasLocks: sharedValue(furniture, (f) => f.estimation_config?.has_locks ?? false),
	fillings: sharedValue(furniture, (f) => f.estimation_config?.number_of_fillers ?? 0),
	finishingEnds: sharedValue(furniture, (f) => f.estimation_config?.number_of_finishing_ends ?? 0),
	countertopGrommets: sharedValue(furniture, (f) => f.countertop_attachments?.grommets ?? 0),
	countertopWallCleats: sharedValue(furniture, (f) => f.countertop_attachments?.wall_cleats ?? 0),
	notes: sharedValue(furniture, (f) => f.estimation_config_comment ?? ""),
	name: sharedValue(furniture, (f) => f.fixed_price_config?.name ?? ""),
	price: sharedValue(furniture, (f) => f.fixed_price_config?.fixed_price ?? null),
	topLevelCategory: sharedValue(furniture, (f) => f.top_level_category?.name ?? null),
	superCategory: sharedValue(furniture, (f) => f.super_category?.name ?? null),
	objectCategoryId: sharedValue(furniture, (f) => f.category?.id ?? null),
	objectCategoryName: sharedValue(furniture, (f) => f.category?.name ?? null),
	status: sharedValue(furniture, (f) => f.status ?? "work_in_progress"),
	hasCustomization: sharedValue(furniture, (f) => f.customization != null),
	customizationNote: sharedValue(furniture, (f) => f.customization?.note ?? ""),
	customizationPriceAdjustment: sharedValue(furniture, (f) => f.customization?.price_adjustment ?? null),
})

/**
 * Whether "Inherit from elevation" can be offered to the WHOLE selection at once (#1390's batch
 * take on REQ-410's single-object reset).
 *
 * Two conditions, both required:
 *
 *  - Clearing the override only reads as ONE shared action if every selected object's OWN elevation
 *    resolves to the SAME delivery date — erefs may differ (a selection spanning several elevations)
 *    as long as the date each would fall back to does not. An elevation with no delivery date
 *    configured counts as agreeing with another elevation that also has none, same as any other
 *    shared-null field elsewhere in this panel.
 *  - At least one selected object needs an ACTUAL override to clear. Without this, a selection that
 *    is already fully inherited (or has no date at all because neither it nor its elevation has one)
 *    would still show the button — a reset a user could click that changes nothing, because there is
 *    nothing to change.
 */
export const canResetBatchDeliveryDate = (furniture: readonly TFurniture[], erefs: readonly TEref[]): boolean => {
	if (furniture.length === 0) return false
	let sharedDateId: number | null | undefined
	let hasOverride = false
	for (const item of furniture) {
		if (item.eref_id == null) return false
		if (item.delivery_date != null && !item.delivery_date.is_inherited) hasOverride = true
		const elevationDate = erefs.find((eref) => eref.id === item.eref_id)?.estimation_config?.delivery_date
		const dateId = elevationDate?.id ?? null
		if (sharedDateId === undefined) sharedDateId = dateId
		else if (sharedDateId !== dateId) return false
	}
	return hasOverride
}

/**
 * Copy for the grey image-preview panel, or `null` when the real catalogue image should show.
 *
 * Only an all-cabinet selection can ever have an image, and only when the whole selection agrees on
 * one model number — the picture is of the model number, not of the objects.
 */
export const batchImagePreviewMessage = (
	kind: TBatchSelectionKind,
	isModelNumberMixed: boolean,
): string | null => {
	if (kind === "countertop") return "Images are not supported for countertop category"
	if (kind === "other") return "Images are not supported for other category"
	// #1346 — a custom definition has no catalogue entry behind it, so there is no image at any size
	// of selection; the "different model numbers" line below would misdescribe why.
	if (kind === "custom") return "Images are not supported for custom object"
	if (kind === "mixed") return "Preview isn't available because the selected items belong to different categories."
	if (isModelNumberMixed) return "Preview isn't available because the selected items use different model numbers."
	return null
}

/**
 * The DISTINCT width/height/depth triples across the selection, in first-appearance order, each
 * carrying how many objects share it. Feeds the batch model-number modal's "Cabinet dimensions"
 * block (which suffixes `(n)` for a triple occurring more than once) and its match scoring.
 */
export const collectDimensionGroups = (furniture: readonly TFurniture[]): TDimensionGroup[] => {
	const byKey = new Map<string, TDimensionGroup>()
	for (const item of furniture) {
		const key = `${item.width ?? ""}|${item.height ?? ""}|${item.depth ?? ""}`
		const existing = byKey.get(key)
		if (existing) {
			existing.count += 1
			continue
		}
		byKey.set(key, { width: item.width, height: item.height, depth: item.depth, count: 1 })
	}
	return [...byKey.values()]
}

/** Header title of the batch panel — "3 objects selected". */
export const buildBatchSelectionTitle = (count: number): string =>
	`${count} object${count === 1 ? "" : "s"} selected`

/**
 * Copy for the confirmation raised before a category change unifies a selection that currently
 * spans more than one category (REQ-403 §4.5). Quoted from the Figma frame.
 */
export const buildBatchCategoryConfirmText = (
	count: number,
	categoryName: string,
): { title: string; description: string } => ({
	title: `Change ${count} object${count === 1 ? "" : "s"} to ${categoryName}?`,
	description: "This makes the selected objects use one common category.",
})

/**
 * Whether the selection already agrees on one category, i.e. whether a category change needs the
 * confirmation above. Compared on the leaf category id, which is `null` for every Other object —
 * so a selection of Others counts as unified.
 */
export const isCategoryUnified = (furniture: readonly TFurniture[]): boolean =>
	!isMixed(sharedValue(furniture, (f) => f.category?.id ?? null))

/**
 * The one leaf category a top-level category resolves to, or `null` when it is ambiguous.
 *
 * The batch category endpoint takes a LEAF `related_category_id`, so picking a top level alone can
 * only commit when that branch of `/categories/` holds exactly one super category with exactly one
 * object category. Otherwise this returns null and the panel just moves its local selects, waiting
 * for the user to pick the lower levels rather than guessing a leaf.
 *
 * MEASURED against the live backend: `Other` is the ONLY top level that resolves — and it does not
 * even come through here, since it maps to `related_category_id: null` directly. `Countertops`
 * does NOT: its single super category `General Countertops` holds TWO leaves (`Standalone
 * Countertop` 3468 and `Countertop with a backsplash` 3469). So the design's headline §4.5 flow —
 * pick top-level `Countertops` on a mixed selection and get the confirmation modal — does not fire
 * from the top-level pick; the user picks Countertops, then an Object level, and the confirmation
 * comes on that second pick. Refusing to guess is the correct behaviour (picking either leaf for the
 * user would silently choose whether their countertops have a backsplash), so this is a note about
 * the design's assumption, not a bug to code around.
 */
export const resolveSingleObjectCategoryId = (
	categories: ICategoriesData | undefined,
	topLevelName: string | null,
): number | null => {
	if (!categories || !topLevelName) return null
	const superCategories = categories[topLevelName]
	if (!superCategories) return null
	const buckets = Object.values(superCategories)
	if (buckets.length !== 1) return null
	const leaves = buckets[0]
	if (!Array.isArray(leaves) || leaves.length !== 1) return null
	return typeof leaves[0]?.id === "number" ? leaves[0].id : null
}

/** The review toggle a multi-object selection offers, or `null` when it may offer none. */
export type TBatchReviewToggle = {
	key: "review" | "unreview"
	label: string
	targetStatus: "work_in_progress" | "reviewed"
}

/**
 * The review toggle for a selection — REQ-369's rule, shared by the floating batch toolbar and the
 * batch edit panel's footer so the two can never drift apart.
 *
 * Shown only when every object already shares the SAME review state: the toggle applies ONE status
 * to all of them, so a selection that disagrees has no honest label. In particular a selection that
 * mixes `in_quotation` with anything else must offer nothing — labelling it "Mark as reviewed" would
 * quietly pull the `in_quotation` objects back out of the quotation, which is exactly what a single
 * object's own sidebar never offers (it shows "Remove from quotation" instead). A uniform
 * `in_quotation` selection is likewise null here: that transition belongs to the explicit
 * add/remove-from-quotation buttons, not to this toggle. Missing status counts as
 * `work_in_progress`.
 */
export const resolveBatchReviewToggle = (furniture: readonly TFurniture[]): TBatchReviewToggle | null => {
	if (furniture.length === 0) return null
	const statuses = new Set(furniture.map((f) => f.status ?? "work_in_progress"))
	if (statuses.size !== 1) return null
	const [only] = [...statuses]
	if (only === "work_in_progress") return { key: "review", label: "Mark as reviewed", targetStatus: "reviewed" }
	if (only === "reviewed") return { key: "unreview", label: "Mark as unreviewed", targetStatus: "work_in_progress" }
	return null
}

/**
 * Toast copy for a `207` partial success from the batch status endpoint, or `null` when nothing
 * failed.
 *
 * `failed` arrives as `reason -> ids`, and that grouping is deliberate: the reason is the ONLY thing
 * that tells the estimator why an object was left behind (no model number, no price for the series,
 * a missing dimension...). Collapsing it to a bare count throws away the one piece of information
 * that says what to fix, so the reasons are surfaced — verbatim, since they are backend strings and
 * this layer cannot know the full set. A count is appended per reason only when there are several,
 * where the split is otherwise unreadable.
 *
 * Only `in_quotation` has preconditions, so this is the only status that can report failures.
 */
export const buildBatchStatusFailureMessage = (failed: Record<string, number[]> | undefined): string | null => {
	const groups = Object.entries(failed ?? {}).filter(([, ids]) => ids.length > 0)
	if (groups.length === 0) return null
	const total = groups.reduce((sum, [, ids]) => sum + ids.length, 0)
	const headline = `${total} object${total === 1 ? "" : "s"} could not be added to the quotation`
	const reasons = groups
		.map(([reason, ids]) => (groups.length === 1 ? reason.trim() : `${reason.trim()} (${ids.length})`))
		.filter((reason) => reason !== "")
		.join("; ")
	return reasons === "" ? headline : `${headline}: ${reasons}`
}

/** The `items=<id>:<version>` query pairs the batch comment DELETE takes. */
export const buildCommentDeleteItems = (furniture: readonly { id: number; version: number }[]): string[] =>
	furniture.map((f) => `${f.id}:${f.version}`)

/**
 * Which of the two customization fields the user actually edited (REQ-431), given the section's
 * per-field shared/baseline values — an OMITTED key, not a copied-over one, for the field that
 * wasn't touched.
 *
 * This is what lets the batch commit avoid the endpoint's all-or-nothing shape: the PUT requires
 * BOTH `note` and `price_adjustment` on every request, so a caller that always sent both would
 * force the untouched field to whatever the LOCAL state happens to hold — its Mixed baseline
 * (`""`/`null`) when the selection actually disagrees, clobbering every object's real, differing
 * value in that field. Returning only the touched key lets the commit instead GROUP the selection
 * by each object's own current value of the omitted field and send one request per group,
 * preserving real per-object differences (see `useBatchFurnitureEdit.commitCustomization`).
 *
 * Checked per field, like the fields themselves: a Mixed (or absent) note is dirty once it holds
 * anything other than `""`, independently of whether the price is Mixed, absent or shared — and
 * vice versa. A shared value is dirty once the field differs from it. The section only reaches an
 * absent/Mixed state's own empty defaults via the `+`, which already wrote `{ note: "",
 * price_adjustment: null }` to the server, so a blur with those same values has nothing new to send
 * — an empty result here means the commit is skipped entirely.
 */
export const resolveCustomizationEdits = (
	sharedNote: TBatchValue<string>,
	sharedPriceAdjustment: TBatchValue<number | null>,
	note: string,
	priceAdjustment: number | null,
): Partial<TFurnitureCustomization> => {
	const baselineNote = isMixed(sharedNote) ? "" : sharedNote
	const baselinePriceAdjustment = isMixed(sharedPriceAdjustment) ? null : sharedPriceAdjustment
	const edits: Partial<TFurnitureCustomization> = {}
	if (note !== baselineNote) edits.note = note
	if (priceAdjustment !== baselinePriceAdjustment) edits.price_adjustment = priceAdjustment
	return edits
}
